import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';

import Loader from '../../../components/Common/Loader/Loader';
import CustomPagination from '../../../components/CustomPagination';
import query from './getBookingAttempts.graphql';

// Human labels for the machine failure-reason slugs written by besthand-api.
const REASON_LABEL = {
  no_payment_method: 'No payment method',
  card_declined: 'Card declined / insufficient funds',
  insufficient_wallet: 'Wallet balance too low',
  out_of_area: 'Outside service area',
  method_unavailable: 'Booking method disabled',
  coupon_invalid: 'Invalid coupon / promo',
  no_location: 'No delivery location',
  no_worker: 'No pro available',
  service_unavailable: 'Service unavailable',
  cash_disabled: 'Cash disabled',
  order_error: 'Order error',
  other: 'Other',
};
const reasonLabel = (r) => REASON_LABEL[r] || r || '-';
const PAYMENT = { 1: 'Cash', 2: 'Card', 3: 'Wallet', 4: 'Apple Pay', 5: 'Google Pay' };
const SOURCE = { app: { t: 'App', c: '#2563eb' }, web: { t: 'Web', c: '#7c3aed' }, manual: { t: 'Manual', c: '#b45309' } };

const card = { border: '1px solid #eee', borderRadius: 10, padding: '12px 16px', minWidth: 150 };

const BookingAttemptsTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [reason, setReason] = useState('');

  const { loading, data, refetch } = useQuery(query, {
    variables: { currentPage, reason: reason || null },
    fetchPolicy: 'network-only',
    ssr: false,
  });

  const d = (data && data.getBookingAttempts) || {};
  const results = d.results || [];
  const count = d.count || 0;
  const byReason = d.summaryByReason || [];
  const byDay = d.summaryByDay || [];
  const totalAll = byReason.reduce((s, x) => s + (x.count || 0), 0);

  const go = (page) => { setCurrentPage(page); refetch({ currentPage: page, reason: reason || null }); };
  const filterBy = (r) => { const nr = reason === r ? '' : r; setReason(nr); setCurrentPage(1); refetch({ currentPage: 1, reason: nr || null }); };

  const fmtDate = (v) => {
    if (!v) return '-';
    const n = Number(v);
    const m = Number.isFinite(n) && String(v).length > 8 ? moment(n) : moment(v);
    return m.isValid() ? m.format('DD-MM-YYYY HH:mm') : String(v);
  };
  const maxDay = Math.max(1, ...byDay.map((x) => x.count || 0));

  return (
    <Loader type={'page'} show={loading}>
      <h1 className="siteAdminPrimaryHeading">Booking drop-offs — failed order attempts</h1>
      <p style={{ color: '#666', marginTop: -6 }}>
        Every time a client tried to order but couldn't complete it (no payment method, card declined,
        out of area, etc.). Total logged: <strong>{totalAll}</strong>.
      </p>

      {/* Counts by reason (click to filter) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, margin: '14px 0 6px' }}>
        {byReason.length === 0 && <span style={{ color: '#999' }}>No failed attempts logged yet.</span>}
        {byReason.map((x) => (
          <div
            key={x.reason}
            onClick={() => filterBy(x.reason)}
            style={{ ...card, cursor: 'pointer', background: reason === x.reason ? '#fef2f2' : '#fff', borderColor: reason === x.reason ? '#ef4444' : '#eee' }}
            title="Click to filter"
          >
            <div style={{ fontSize: 22, fontWeight: 700, color: '#ef4444' }}>{x.count}</div>
            <div style={{ fontSize: 13, color: '#555' }}>{reasonLabel(x.reason)}</div>
          </div>
        ))}
        {reason && (
          <div onClick={() => filterBy(reason)} style={{ ...card, cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#2563eb' }}>
            Clear filter ✕
          </div>
        )}
      </div>

      {/* 14-day trend */}
      {byDay.length > 0 && (
        <div style={{ margin: '10px 0 18px' }}>
          <div style={{ fontSize: 13, color: '#777', marginBottom: 6 }}>Last 14 days</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 70 }}>
            {byDay.map((x) => (
              <div key={x.day} style={{ textAlign: 'center' }} title={`${x.day}: ${x.count}`}>
                <div style={{ width: 22, height: Math.round((x.count / maxDay) * 56) + 4, background: '#ef4444', borderRadius: 3 }} />
                <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{moment(x.day).format('DD/MM')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detail table */}
      {!loading && (
        results.length === 0 ? (
          <p>No attempts to show{reason ? ` for "${reasonLabel(reason)}"` : ''}.</p>
        ) : (
          <>
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Source</th>
                  <th scope="col">Client</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Payment</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Location</th>
                  <th scope="col">Order</th>
                </tr>
              </thead>
              <tbody>
                {results.map((item) => (
                  <tr key={item.id}>
                    <td data-label="When">{fmtDate(item.createdAt)}</td>
                    <td data-label="Source">
                      <span style={{ color: (SOURCE[item.source] || {}).c || '#666', fontWeight: 600, fontSize: 12 }}>
                        {(SOURCE[item.source] || {}).t || item.source || '-'}
                      </span>
                    </td>
                    <td data-label="Client">
                      <div>{item.clientName || '-'}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{item.clientEmail || ''}</div>
                    </td>
                    <td data-label="Reason">
                      <span style={{ background: '#fef2f2', color: '#b91c1c', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>
                        {reasonLabel(item.failureReason)}
                      </span>
                    </td>
                    <td data-label="Payment">{PAYMENT[item.paymentType] || (item.paymentType != null ? item.paymentType : '-')}</td>
                    <td data-label="Amount">{item.amount != null ? `${item.currency || ''} ${Number(item.amount).toFixed(2)}` : '-'}</td>
                    <td data-label="Location">{item.publicLocation || '-'}</td>
                    <td data-label="Order">{item.orderId || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {count > 10 && (
              <CustomPagination
                total={count}
                currentPage={currentPage}
                defaultCurrent={1}
                defaultPageSize={10}
                change={go}
                paginationLabel="attempts"
              />
            )}
          </>
        )
      )}
    </Loader>
  );
};

export default BookingAttemptsTable;
